import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PROFILE_STORES_SELF,
  PROFILE_STORES_UPDATE,
  PROFILE_STORES_UPLOAD_IMAGE,
  PROFILE_STORES_UPLOAD_GALLERY,
  PROFILE_STORES_UPLOAD_COVER_IMAGE,
  PROFILE_STORES_IMAGE_URL,
  PROFILE_STORES_DELETE_IMAGE,
  PROFILE_STORES_DELETE_COVER_IMAGE,
  PROFILE_STORES_APPOINTMENTS_SETTINGS
} from '../../../../shared/constants/endpoints';
import useApiRequest from '../../../../shared/hooks/useApiRequest';
import { computeStoreProfileReadiness, getCustomerProfile, persistStoreProfileReadiness } from '../../../../shared/utils/storeProfileReadiness';

const PROFILE_DESCRIPTION_KEY_PREFIX = 'zopeo_store_profile_description_';
const DEFAULT_CATEGORY = 'RETAIL';

const DEFAULT_FORM_DATA = {
  storeName: '',
  email: '',
  phone: '',
  storeCode: '',
  storeCategory: DEFAULT_CATEGORY,
  address: '',
  pincode: '',
  latitude: null,
  longitude: null,
  description: '',
  headline: '',
  signatureHighlights: [],
  valueProps: [],
  galleryImages: [],
  imgUploaded: false,
  weekdayOpen: '09:00',
  weekdayClose: '21:00',
  weekendOpen: '10:00',
  weekendClose: '22:00'
};

const readStoredDescription = (storeUuid) => {
  if (!storeUuid || typeof window === 'undefined') {
    return '';
  }
  return window.localStorage.getItem(`${PROFILE_DESCRIPTION_KEY_PREFIX}${storeUuid}`) || '';
};

const clearStoredDescription = (storeUuid) => {
  if (!storeUuid || typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(`${PROFILE_DESCRIPTION_KEY_PREFIX}${storeUuid}`);
};

const mapStorePayloadToForm = (store = {}) => {
  const customerProfile = getCustomerProfile(store);
  return {
    storeName: store.storeName || '',
    email: store.email || '',
    phone: store.phone || '',
    storeCode: store.storeCode || '',
    storeCategory: String(store.clientType || DEFAULT_CATEGORY).toUpperCase(),
    address: store.area || '',
    pincode: store.pincode || '',
    latitude: store.latitude != null ? store.latitude : null,
    longitude: store.longitude != null ? store.longitude : null,
    description: customerProfile.shortDescription,
    headline: customerProfile.headline,
    signatureHighlights: customerProfile.signatureHighlights,
    valueProps: customerProfile.valueProps,
    galleryImages: customerProfile.gallery,
    imgUploaded: store.imgUploaded || false,
    weekdayOpen: store.weekdayOpen || '09:00',
    weekdayClose: store.weekdayClose || '21:00',
    weekendOpen: store.weekendOpen || '10:00',
    weekendClose: store.weekendClose || '22:00'
  };
};

const formsEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const toMinutes = (value) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!match) {
    return null;
  }
  return Number(match[1]) * 60 + Number(match[2]);
};

// "Warn but allow": store hours may be narrowed freely, but if that leaves the
// appointment booking window hanging outside the new store hours, we flag it so
// the merchant knows to update their booking hours. Returns the day labels that
// now fall outside store hours.
const findHoursOutsideStore = (storeHours, apptSettings) => {
  if (!apptSettings) {
    return [];
  }
  const offenders = [];
  const check = (label, apptOpen, apptClose, storeOpen, storeClose) => {
    const ao = toMinutes(apptOpen);
    const ac = toMinutes(apptClose);
    const so = toMinutes(storeOpen);
    const sc = toMinutes(storeClose);
    if (ao == null || ac == null || so == null || sc == null) {
      return;
    }
    if (ao < so || ac > sc) {
      offenders.push(label);
    }
  };
  check('weekday', apptSettings.weekdayOpen, apptSettings.weekdayClose, storeHours.weekdayOpen, storeHours.weekdayClose);
  check('weekend', apptSettings.weekendOpen, apptSettings.weekendClose, storeHours.weekendOpen, storeHours.weekendClose);
  return offenders;
};

const normalizeCategory = (value) => {
  const supported = new Set(['RETAIL', 'CLINIC', 'SALON', 'FOOD_COURT']);
  const normalized = String(value || DEFAULT_CATEGORY).toUpperCase();
  return supported.has(normalized) ? normalized : DEFAULT_CATEGORY;
};

const extractStoreMetaData = (store) => {
  const rawMeta = store?.storeMetaData;
  if (!rawMeta) {
    return {};
  }
  if (typeof rawMeta === 'string') {
    try {
      return JSON.parse(rawMeta);
    } catch (error) {
      return {};
    }
  }
  if (typeof rawMeta === 'object') {
    return rawMeta;
  }
  return {};
};

const resolveProfileImageUrl = (store) => {
  if (!store || typeof store !== 'object') {
    return '';
  }
  const meta = extractStoreMetaData(store);
  const candidates = [
    store.profileImageUrl,
    store.imageUrl,
    store.logoUrl,
    meta.profileImageUrl,
    meta.imageUrl,
    meta.logoUrl
  ];
  const resolved = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return resolved ? resolved.trim() : '';
};

const resolveCoverImageUrl = (store) => {
  if (!store || typeof store !== 'object') {
    return '';
  }
  const meta = extractStoreMetaData(store);
  const customerProfile = meta.customerProfile && typeof meta.customerProfile === 'object'
    ? meta.customerProfile
    : {};
  const candidates = [
    store.coverImageUrl,
    customerProfile.coverImageUrl,
    meta.coverImageUrl
  ];
  const resolved = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return resolved ? resolved.trim() : '';
};

const mergeGalleryImages = (existingImages, nextImages) => {
  const merged = [...(Array.isArray(nextImages) ? nextImages : []), ...(Array.isArray(existingImages) ? existingImages : [])];
  return Array.from(new Set(merged.map((value) => String(value || '').trim()).filter(Boolean)));
};

const useStoreProfile = ({ request, storeToken, storeUuid, onToast }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM_DATA);
  const [uploadedJustNow, setUploadedJustNow] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [galleryPendingItems, setGalleryPendingItems] = useState([]);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [legacyDescriptionNotice, setLegacyDescriptionNotice] = useState(false);
  const [locationValidationError, setLocationValidationError] = useState(null);
  const onToastRef = useRef(onToast);
  const requestRef = useRef(request);
  const objectPreviewUrlRef = useRef('');
  const coverPreviewUrlRef = useRef('');
  const galleryPendingUrlsRef = useRef([]);
  const formDataRef = useRef(DEFAULT_FORM_DATA);

  const {
    execute: executeFetch
  } = useApiRequest();

  const {
    execute: executeSave,
    loading: isSaving
  } = useApiRequest();

  const {
    execute: executeUpload,
    loading: isUploading
  } = useApiRequest();
  const {
    execute: executeGalleryUpload,
    loading: isUploadingGallery
  } = useApiRequest();

  const {
    execute: executeCoverUpload,
    loading: isUploadingCover
  } = useApiRequest();

  const {
    execute: executeDeleteImage,
    loading: isDeletingImage
  } = useApiRequest();

  const {
    execute: executeDeleteCoverImage,
    loading: isDeletingCover
  } = useApiRequest();

  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const loadProfile = useCallback(async ({ silent = false, preserveGallery = false, requestName = 'store-profile' } = {}) => {
    const requestClient = requestRef.current;
    if (typeof requestClient !== 'function' || !storeToken) {
      if (!silent) {
        setIsBootstrapping(false);
      }
      return null;
    }

    try {
      const data = await executeFetch(() => requestClient(PROFILE_STORES_SELF, {
        method: 'GET',
        requestName
      }), { logModule: 'store-profile.fetch' });

      if (!data?.data) {
        return null;
      }

      const mappedForm = mapStorePayloadToForm(data.data);
      mappedForm.storeCategory = normalizeCategory(mappedForm.storeCategory);
      if (preserveGallery) {
        mappedForm.galleryImages = mergeGalleryImages(formDataRef.current.galleryImages, mappedForm.galleryImages);
      }
      const storedDescription = readStoredDescription(data.data.storeUuid);
      const shouldUseLegacyDescription = !mappedForm.description && storedDescription.trim().length > 0;
      if (shouldUseLegacyDescription) {
        mappedForm.description = storedDescription.trim();
        setLegacyDescriptionNotice(true);
      } else {
        setLegacyDescriptionNotice(false);
        if (mappedForm.description) {
          clearStoredDescription(data.data.storeUuid);
        }
      }
      setFormData(mappedForm);
      setInitialFormData(mappedForm);
      setLocationValidationError(null);
      setUploadedJustNow(false);
      setCoverImageUrl(resolveCoverImageUrl(data.data));

      try {
        const imgData = await executeFetch(() => requestClient(PROFILE_STORES_IMAGE_URL, {
          method: 'GET',
          requestName: 'store-image-url'
        }), { logModule: 'store-profile.image-url' });
        const cachedUrl = imgData?.data?.url;
        if (cachedUrl) {
          setProfileImageUrl(cachedUrl);
        } else {
          setProfileImageUrl(resolveProfileImageUrl(data.data));
        }
      } catch {
        setProfileImageUrl(resolveProfileImageUrl(data.data));
      }

      return mappedForm;
    } catch (error) {
      if (!silent) {
        onToastRef.current?.({ message: 'Failed to load store profile', variant: 'error' });
      }
      throw error;
    } finally {
      if (!silent) {
        setIsBootstrapping(false);
      }
    }
  }, [executeFetch, storeToken]);

  useEffect(() => {
    let isCancelled = false;

    setIsBootstrapping(true);
    loadProfile().catch(() => {
      if (isCancelled) {
        return;
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [loadProfile]);

  useEffect(() => () => {
    if (objectPreviewUrlRef.current) {
      URL.revokeObjectURL(objectPreviewUrlRef.current);
      objectPreviewUrlRef.current = '';
    }
    if (coverPreviewUrlRef.current) {
      URL.revokeObjectURL(coverPreviewUrlRef.current);
      coverPreviewUrlRef.current = '';
    }
    galleryPendingUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    galleryPendingUrlsRef.current = [];
  }, []);

  const handleChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleTimeChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleProfileEntryChange = useCallback((field, index, value) => {
    setFormData((prev) => {
      const entries = Array.isArray(prev[field]) ? [...prev[field]] : [];
      entries[index] = value;
      return { ...prev, [field]: entries };
    });
  }, []);

  const handleAddProfileEntry = useCallback((field, limit) => {
    setFormData((prev) => {
      const entries = Array.isArray(prev[field]) ? [...prev[field]] : [];
      if (entries.length >= limit) {
        return prev;
      }
      return { ...prev, [field]: [...entries, ''] };
    });
  }, []);

  const handleRemoveProfileEntry = useCallback((field, index) => {
    setFormData((prev) => {
      const entries = Array.isArray(prev[field]) ? [...prev[field]] : [];
      entries.splice(index, 1);
      return { ...prev, [field]: entries };
    });
  }, []);

  const handleLocationChange = useCallback(({ address, latitude, longitude }) => {
    setFormData((prev) => ({
      ...prev,
      address,
      latitude,
      longitude
    }));
  }, []);

  const handleLocationValidationChange = useCallback(({ isValid, error }) => {
    setLocationValidationError(isValid ? null : (error || 'Location must be validated before saving.'));
  }, []);

  const handleImageUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (file.size > 1048576) {
      onToast({ message: 'Image size must be less than 1MB', variant: 'error' });
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      onToast({ message: 'Only JPG and PNG images are allowed', variant: 'error' });
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    const previousProfileImageUrl = profileImageUrl;
    const previewObjectUrl = URL.createObjectURL(file);
    if (objectPreviewUrlRef.current) {
      URL.revokeObjectURL(objectPreviewUrlRef.current);
    }
    objectPreviewUrlRef.current = previewObjectUrl;
    setProfileImageUrl(previewObjectUrl);

    try {
      const response = await executeUpload(() => request(PROFILE_STORES_UPLOAD_IMAGE, {
        method: 'POST',
        body: uploadFormData,
        isFileUpload: true
      }), { logModule: 'store-profile.upload' });

      onToast({ message: 'Image uploaded successfully!', variant: 'success' });
      setFormData((prev) => {
        const next = { ...prev, imgUploaded: true };
        setInitialFormData((previousInitial) => ({ ...previousInitial, imgUploaded: true }));
        return next;
      });
      setUploadedJustNow(true);
      const resolvedProfileImageUrl = resolveProfileImageUrl(response?.data);
      if (resolvedProfileImageUrl) {
        setProfileImageUrl(resolvedProfileImageUrl);
      }
    } catch (error) {
      setProfileImageUrl(previousProfileImageUrl);
      onToast({ message: 'Failed to upload image', variant: 'error' });
    }
  }, [executeUpload, onToast, profileImageUrl, request, storeToken]);

  const handleDeleteImage = useCallback(async () => {
    try {
      await executeDeleteImage(() => request(PROFILE_STORES_DELETE_IMAGE, {
        method: 'DELETE'
      }), { logModule: 'store-profile.delete-image' });

      setProfileImageUrl('');
      setFormData((prev) => {
        const next = { ...prev, imgUploaded: false };
        setInitialFormData((previousInitial) => ({ ...previousInitial, imgUploaded: false }));
        return next;
      });
      setUploadedJustNow(false);
      onToast({ message: 'Profile image removed', variant: 'success' });
    } catch {
      onToast({ message: 'Failed to remove profile image', variant: 'error' });
    }
  }, [executeDeleteImage, onToast, request, storeToken]);

  const handleCoverImageUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (file.size > 1048576) {
      onToast({ message: 'Cover image size must be less than 1MB', variant: 'error' });
      event.target.value = '';
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      onToast({ message: 'Only JPG and PNG cover images are allowed', variant: 'error' });
      event.target.value = '';
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    const previousCoverImageUrl = coverImageUrl;
    const previewObjectUrl = URL.createObjectURL(file);
    if (coverPreviewUrlRef.current) {
      URL.revokeObjectURL(coverPreviewUrlRef.current);
    }
    coverPreviewUrlRef.current = previewObjectUrl;
    setCoverImageUrl(previewObjectUrl);

    try {
      const response = await executeCoverUpload(() => request(PROFILE_STORES_UPLOAD_COVER_IMAGE, {
        method: 'POST',
        body: uploadFormData,
        isFileUpload: true
      }), { logModule: 'store-profile.cover-upload' });

      const resolvedCoverImageUrl = resolveCoverImageUrl(response?.data);
      if (resolvedCoverImageUrl) {
        setCoverImageUrl(resolvedCoverImageUrl);
      }
      onToast({ message: 'Cover photo uploaded successfully', variant: 'success' });
    } catch {
      setCoverImageUrl(previousCoverImageUrl);
      onToast({ message: 'Failed to upload cover photo', variant: 'error' });
    } finally {
      event.target.value = '';
    }
  }, [coverImageUrl, executeCoverUpload, onToast, request, storeToken]);

  const handleDeleteCoverImage = useCallback(async () => {
    try {
      const response = await executeDeleteCoverImage(() => request(PROFILE_STORES_DELETE_COVER_IMAGE, {
        method: 'DELETE'
      }), { logModule: 'store-profile.delete-cover-image' });

      setCoverImageUrl(resolveCoverImageUrl(response?.data));
      onToast({ message: 'Cover photo removed', variant: 'success' });
    } catch {
      onToast({ message: 'Failed to remove cover photo', variant: 'error' });
    }
  }, [executeDeleteCoverImage, onToast, request, storeToken]);

  const handleGalleryUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    for (const file of files) {
      if (file.size > 1048576) {
        onToast({ message: 'Each gallery image must be less than 1MB', variant: 'error' });
        return;
      }
      if (!validTypes.includes(file.type)) {
        onToast({ message: 'Only JPG and PNG images are allowed', variant: 'error' });
        return;
      }
    }

    const uploadFormData = new FormData();
    files.forEach((file) => uploadFormData.append('images', file));
    const previewUrls = files.map((file) => URL.createObjectURL(file));
    galleryPendingUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    galleryPendingUrlsRef.current = previewUrls;
    setGalleryPendingItems(previewUrls.map((url) => ({ id: url, url })));

    try {
      await executeGalleryUpload(() => request(PROFILE_STORES_UPLOAD_GALLERY, {
        method: 'POST',
        body: uploadFormData,
        isFileUpload: true
      }), { logModule: 'store-profile.gallery-upload' });
      await loadProfile({ silent: true, preserveGallery: true, requestName: 'store-profile.gallery-refresh' });
      galleryPendingUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      galleryPendingUrlsRef.current = [];
      setGalleryPendingItems([]);
      onToast({ message: 'Gallery images added', variant: 'success' });
    } catch {
      onToast({ message: 'Failed to upload gallery images', variant: 'error' });
    } finally {
      event.target.value = '';
    }
  }, [executeGalleryUpload, loadProfile, onToast, request, storeToken]);

  const handleRemoveGalleryImage = useCallback((imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((item) => item !== imageUrl)
    }));
  }, []);

  const handleCancel = useCallback(() => {
    setFormData(initialFormData);
    setLocationValidationError(null);
    setLegacyDescriptionNotice(false);
    onToast?.({ message: 'Changes discarded', variant: 'info' });
  }, [initialFormData, onToast]);

  const normalizeProfileEntries = useCallback((value) => {
    const rawEntries = Array.isArray(value) ? value : String(value || '').split(/\n|,/);
    return rawEntries
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }, []);

  const handleSave = useCallback(async () => {
    const trimmedName = formData.storeName.trim();
    if (!trimmedName) {
      onToast({ message: 'Store name is required', variant: 'error' });
      return;
    }

    const normalizedCategory = normalizeCategory(formData.storeCategory);
    const payload = {
      storeUuid,
      storeName: trimmedName,
      email: formData.email,
      phone: formData.phone,
      clientType: normalizedCategory,
      area: formData.address.trim(),
      pincode: formData.pincode,
      latitude: formData.latitude,
      longitude: formData.longitude,
      weekdayOpen: formData.weekdayOpen,
      weekdayClose: formData.weekdayClose,
      weekendOpen: formData.weekendOpen,
      weekendClose: formData.weekendClose,
      customerProfile: {
        headline: formData.headline.trim(),
        shortDescription: formData.description.trim(),
        signatureHighlights: normalizeProfileEntries(formData.signatureHighlights),
        valueProps: normalizeProfileEntries(formData.valueProps),
        gallery: Array.isArray(formData.galleryImages) ? formData.galleryImages : []
      }
    };

    try {
      await executeSave(() => request(PROFILE_STORES_UPDATE(storeUuid), {
        method: 'PUT',
        body: JSON.stringify(payload)
      }), { logModule: 'store-profile.save' });

      clearStoredDescription(storeUuid);
      const nextSnapshot = {
        ...formData,
        storeName: trimmedName,
        address: formData.address.trim(),
        description: formData.description.trim(),
        headline: formData.headline.trim(),
        signatureHighlights: normalizeProfileEntries(formData.signatureHighlights),
        valueProps: normalizeProfileEntries(formData.valueProps),
        galleryImages: Array.isArray(formData.galleryImages) ? formData.galleryImages : [],
        storeCategory: normalizedCategory
      };
      setFormData(nextSnapshot);
      setInitialFormData(nextSnapshot);
      setLocationValidationError(null);
      setLegacyDescriptionNotice(false);
      onToast({ message: 'Store profile updated successfully', variant: 'success' });

      // Warn (don't block) if the new store hours now leave the appointment
      // booking window hanging outside them — the merchant should narrow their
      // booking hours to match.
      try {
        const apptData = await request(PROFILE_STORES_APPOINTMENTS_SETTINGS, {
          method: 'GET',
          requestName: 'store-profile-appt-hours-check'
        });
        const offenders = findHoursOutsideStore(payload, apptData?.data);
        if (offenders.length > 0) {
          onToast({
            message: `Your ${offenders.join(' and ')} appointment hours now fall outside store hours. Update your booking hours in Appointments to match.`,
            variant: 'warning'
          });
        }
      } catch (warnError) {
        // Non-critical: the store save already succeeded.
      }
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to update store profile';
      onToast({ message, variant: 'error' });
    }
  }, [executeSave, formData, normalizeProfileEntries, onToast, request, storeToken, storeUuid]);

  const hasChanges = useMemo(() => !formsEqual(formData, initialFormData), [formData, initialFormData]);
  const canSave = useMemo(() => {
    if (isSaving || isUploading || isUploadingGallery || !formData.storeName.trim() || locationValidationError) {
      return false;
    }
    // Reject save if address exists but coordinates are missing
    // (Store Location Editor requirement: address must have valid coordinates)
    if (formData.address && formData.address.trim() && (!formData.latitude || !formData.longitude)) {
      return false;
    }
    return hasChanges;
  }, [formData.storeName, formData.address, formData.latitude, formData.longitude, hasChanges, isSaving, isUploading, isUploadingGallery, locationValidationError]);

  const loading = useMemo(() => isBootstrapping, [isBootstrapping]);
  const readiness = useMemo(() => computeStoreProfileReadiness({
    storeMetaData: {
      customerProfile: {
        headline: formData.headline,
        shortDescription: formData.description,
        signatureHighlights: normalizeProfileEntries(formData.signatureHighlights),
        valueProps: normalizeProfileEntries(formData.valueProps),
        gallery: Array.isArray(formData.galleryImages) ? formData.galleryImages : []
      }
    },
    imgUploaded: formData.imgUploaded,
    profileImageUrl
  }), [formData.description, formData.galleryImages, formData.headline, formData.imgUploaded, formData.signatureHighlights, formData.valueProps, normalizeProfileEntries, profileImageUrl]);

  useEffect(() => {
    persistStoreProfileReadiness(storeUuid, readiness);
  }, [readiness, storeUuid]);

  return {
    loading,
    formData,
    isSaving,
    isUploading,
    isUploadingGallery,
    isUploadingCover,
    isDeletingImage,
    isDeletingCover,
    uploadedJustNow,
    profileImageUrl,
    coverImageUrl,
    galleryPendingItems,
    hasChanges,
    canSave,
    locationValidationError,
    legacyDescriptionNotice,
    readiness,
    handleChange,
    handleTimeChange,
    handleProfileEntryChange,
    handleAddProfileEntry,
    handleRemoveProfileEntry,
    handleLocationChange,
    handleLocationValidationChange,
    handleImageUpload,
    handleDeleteImage,
    handleCoverImageUpload,
    handleDeleteCoverImage,
    handleGalleryUpload,
    handleRemoveGalleryImage,
    handleCancel,
    handleSave
  };
};

export default useStoreProfile;
